import { useEffect, useState } from 'react';
import { Clock, Loader2 } from 'lucide-react';
import { useCreateAttendance } from '../../../services/httpServices/queries';
import { EmployeeSearchSelect } from '../../../components/ui/EmployeeSearchSelect';
import { HrModal } from './HrModal';
import { DatePicker, TimePicker } from '../../../components/ui/DatePicker';

const ATTENDANCE_STATUSES = [
  { value: 'PRESENT', label: 'Present' },
  { value: 'ABSENT', label: 'Absent' },
  { value: 'LATE', label: 'Late' },
  { value: 'HALF_DAY', label: 'Half Day' },
  { value: 'ON_LEAVE', label: 'On Leave' },
  { value: 'HOLIDAY', label: 'Holiday' },
];

const today = () => new Date().toISOString().split('T')[0];

const inputClass =
  'w-full px-3 py-2 border border-border rounded-lg bg-background text-text text-sm focus:outline-none focus:ring-2 focus:ring-primary';

interface AttendanceModalProps {
  open: boolean;
  onClose: () => void;
}

export function AttendanceModal({ open, onClose }: AttendanceModalProps) {
  const createAttendance = useCreateAttendance();

  const [employeeId, setEmployeeId] = useState('');
  const [date, setDate] = useState(today());
  const [status, setStatus] = useState('PRESENT');
  const [checkIn, setCheckIn] = useState('');
  const [checkOut, setCheckOut] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setEmployeeId('');
    setDate(today());
    setStatus('PRESENT');
    setCheckIn('');
    setCheckOut('');
    setNotes('');
    setError('');
  }, [open]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!employeeId) {
      setError('Please select an employee');
      return;
    }
    setError('');

    const payload: Record<string, unknown> = { employeeId, date, status };
    if (checkIn) payload.checkIn = `${date}T${checkIn}:00`;
    if (checkOut) payload.checkOut = `${date}T${checkOut}:00`;
    if (notes.trim()) payload.notes = notes.trim();

    createAttendance.mutate(payload, { onSuccess: () => onClose() });
  };

  return (
    <HrModal
      open={open}
      onClose={onClose}
      title="Mark Attendance"
      subtitle="Record a single employee's attendance for a day"
      icon={Clock}
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm text-text-muted hover:text-text transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="attendance-form"
            disabled={createAttendance.isPending}
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium flex items-center gap-2 disabled:opacity-50"
          >
            {createAttendance.isPending && <Loader2 size={16} className="animate-spin" />}
            Save Attendance
          </button>
        </>
      }
    >
      <form id="attendance-form" onSubmit={handleSubmit} className="space-y-4">
        <EmployeeSearchSelect
          value={employeeId}
          onChange={(id) => {
            setEmployeeId(id);
            if (id) setError('');
          }}
          label="Employee"
          required
          error={error}
        />

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-text mb-1">
              Date <span className="text-danger">*</span>
            </label>
            <DatePicker
              required
              value={date}
              max={today()}
              onChange={setDate}
              className={inputClass}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-text mb-1">Status</label>
            <select value={status} onChange={(e) => setStatus(e.target.value)} className={inputClass}>
              {ATTENDANCE_STATUSES.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-text mb-1">Check In</label>
            <TimePicker value={checkIn} onChange={setCheckIn} className={inputClass} />
          </div>
          <div>
            <label className="block text-sm font-medium text-text mb-1">Check Out</label>
            <TimePicker value={checkOut} onChange={setCheckOut} className={inputClass} />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-text mb-1">Notes</label>
          <input
            type="text"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Optional note"
            className={inputClass}
          />
        </div>
      </form>
    </HrModal>
  );
}
